import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import YouTubeIcon from "@mui/icons-material/YouTube";
import {
  fetchAutomaticResponseForListing,
  getDefaultEmailTemplates,
} from "./DataServices/AutomaticResponsesDataServices";
import { saveListing } from "./DataServices/ApartmentsListingsDataServices";
import { IListing } from "./Home";
import Notification from "./Notification";
import Loading from "./Loading";

interface IAutomaticResponse {
  id: string;
  listingID: string;
  emailBody: string;
}

interface IAddListing {
  open: boolean;
  setOpen: (open: boolean) => void;
  editListings: IListing[];
  setEditListings: React.Dispatch<React.SetStateAction<IListing[]>>;
  refreshApartments: boolean;
  onModalClose: () => void;
}

export default function AddListing({
  open,
  setOpen,
  editListings,
  setEditListings,
  onModalClose,
}: IAddListing) {
  const [listing, setListing] = useState<IListing>({
    id: uuidv4(),
    idApartment: uuidv4(),
    title: "",
    zillowTitle: "",
    description: "",
    rent: "",
    moveInDate: "",
    showings: "",
    areAvailableAppointmentsSet: false,
    isSelected: false,
    isVacant: false,
    building: "",
    line: "",
    floor: 0,
    numOfBedrooms: 0,
    currentRent: 0.0,
    automaticEmail: true,
    accessCode: "",
    appfolioLink: "",
    youTubeLink: "",
    workflowType: 0,
  });

  const [multiSelectedListings, setMultiSelectedListings] = useState(false);
  const [listingBodyEmail, setListingBodyEmail] = useState("");
  const cancelButtonRef = useRef(null);

  //the below 4 states are used to capture if there are changes to the listing details and/or the email body
  const [listingDetailsChanged, setListingDetailsChanged] = useState(false);
  const [emailBodyChanged, setEmailBodyChanged] = useState(false);
  const [isVacantChecked, setIsVacantChecked] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [nameTag, setNameTag] = useState(false);
  const [applinkTag, setAppLinkTag] = useState(false);
  const [apartmentNameTag, setApartmentNameTag] = useState(false);
  const [buildingNameTag, setBuildingNameTag] = useState(false);
  const [accesscodesTag, setAccessCodeTag] = useState(false);
  const [notificationMessageType, setNotificationMessageType] =
    useState("success");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (editListings?.length > 1) {
        setMultiSelectedListings(true);
        setListing({
          id: uuidv4(),
          idApartment: uuidv4(),
          title: "",
          zillowTitle: "",
          description: "",
          rent: "",
          moveInDate: "",
          showings: "",
          areAvailableAppointmentsSet: false,
          isSelected: false,
          isVacant: false,
          building: "",
          line: "",
          floor: 0,
          numOfBedrooms: 0,
          currentRent: 0.0,
          automaticEmail: true,
          accessCode: "",
          appfolioLink: "",
          youTubeLink: "",
          workflowType: 0,
        });
      } else if (editListings?.length == 1) {
        setMultiSelectedListings(false);
        setListing({
          id: editListings[0].id,
          idApartment: editListings[0].idApartment,
          title: editListings[0].title,
          zillowTitle: editListings[0].zillowTitle,
          description: editListings[0].description,
          rent: editListings[0].rent,
          moveInDate: editListings[0].isVacant
            ? ""
            : editListings[0].moveInDate,
          showings: "",
          areAvailableAppointmentsSet: false,
          isSelected: false,
          isVacant: editListings[0].isVacant,
          building: editListings[0].building,
          line: editListings[0].line,
          floor: editListings[0].floor,
          numOfBedrooms: editListings[0].numOfBedrooms,
          currentRent: editListings[0].currentRent,
          automaticEmail: editListings[0].automaticEmail,
          accessCode: editListings[0].accessCode,
          appfolioLink: editListings[0].appfolioLink,
          youTubeLink: editListings[0].youTubeLink,
          workflowType: editListings[0].workflowType,
        });

        setIsVacantChecked(editListings[0].isVacant);
      } else {
        setMultiSelectedListings(false);
        setListing({
          id: uuidv4(),
          idApartment: uuidv4(),
          title: "",
          zillowTitle: "",
          description: "",
          rent: "",
          moveInDate: "",
          showings: "",
          areAvailableAppointmentsSet: false,
          isSelected: false,
          isVacant: false,
          building: "",
          line: "",
          floor: 0,
          numOfBedrooms: 0,
          currentRent: 0.0,
          automaticEmail: true,
          accessCode: "",
          appfolioLink: "",
          youTubeLink: "",
          workflowType: 0,
        });
      }

      const fetchData = async () => {
        try {
          if (editListings.length > 0) {
            // in that case get the email body of one listing in order to change it for all
            const data = await fetchAutomaticResponseForListing(
              editListings[0].id,
            );
            if (data !== null) {
              setListingBodyEmail(data.emailBody || data.EmailBody || "");
            } else {
              setListingBodyEmail("");
            }
          } else {
            // For new listings, populate with the default AutoResponse template from server
            try {
              const defaults = await getDefaultEmailTemplates();
              let defaultBody = "";
              if (Array.isArray(defaults)) {
                const auto = defaults.find(
                  (d: any) =>
                    (d.emailType || d.EmailType || "").toLowerCase() ===
                    "autoresponse",
                );
                defaultBody = auto?.emailBody || auto?.EmailBody || "";
              } else if (defaults) {
                defaultBody = defaults.emailBody || defaults.EmailBody || "";
              }
              setListingBodyEmail(defaultBody);
            } catch (err) {
              setListingBodyEmail("");
            }
          }
        } catch (error) {}
      };

      // Check if the component is open, then fetch the data
      fetchData();
    }
  }, [open]);

  async function SaveChanges() {
    try {
      setLoading(true);
      if (listingDetailsChanged && !multiSelectedListings) {
        const initialListings: IListing[] = [];
        const updatedListings: IListing[] = [...initialListings, listing];

        await saveListing(updatedListings);
      }

      setOpen(false);

      onModalClose();
      setLoading(false);
      setIsVacantChecked(false);
      setNotificationMessage("Apartment Added Successfully!");
      if (editListings.length !== 0) {
        setNotificationMessage("Apartment Edited Successfully!");
      }
      setNotificationMessageType("success");
      setOpenNotification(true);
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
    } catch (error) {
      setLoading(false);
      setNotificationMessage("Failed to add Apartment!");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
    }
  }

  const handleCheckboxChange = (event: any) => {
    setListing((prevListing: IListing) => ({
      ...prevListing,
      isVacant: !isVacantChecked,
      moveInDate: "",
    }));
    setListingDetailsChanged(true);
    setIsVacantChecked(!isVacantChecked);
  };

  const handleInputChange = (fieldName: string, event: any) => {
    if (fieldName == "listingBodyEmail") {
      setEmailBodyChanged(true);
      setListingBodyEmail(event.target.value);
    } else {
      setListingDetailsChanged(true);
      const { value } = event.target;
      const numericFields = [
        "floor",
        "numOfBedrooms",
        "currentRent",
        "workflowType",
      ];
      setListing((prevListing) => ({
        ...prevListing,
        [fieldName]: numericFields.includes(fieldName) ? Number(value) : value,
      }));
    }
  };

  let isDisabled = multiSelectedListings
    ? false
    : listing.title === "" || listing.building === "";
  if (!listing.moveInDate.length && !listing.isVacant) {
    isDisabled = true;
  }

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity " />
          </Transition.Child>

          <div className="fixed inset-0 z-10 flex items-center justify-center">
            <div className="h-[80vh] w-[90vw] bg-white shadow rounded-md overflow-y-auto">
              <div className="flex min-h-full overflow-hidden text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden bg-white px-4 pb-4 pt-2 text-left transition-all sm:my-3 sm:w-full sm:max-w-full sm:p-6">
                    <div>
                      <div className="mb-3">
                        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                          <CheckIcon
                            className=" h-6 w-6 text-green-600"
                            aria-hidden="true"
                          />
                        </div>
                        <div className=" text-center sm:mt-5">
                          <Dialog.Title
                            as="h3"
                            className=" mt-0 mb-5 text-base font-semibold leading-2 text-gray-900"
                          >
                            {editListings.length == 0
                              ? "Add Apartment"
                              : "Edit Apartment"}{" "}
                          </Dialog.Title>
                          <div></div>
                        </div>
                      </div>
                      {!multiSelectedListings && (
                        <div>
                          {/*Title details*/}
                          <div className="flex flex-wrap gap-8">
                            <div className="bg-gray-100 w-[48%] p-6 h-auto rounded-md space-y-4">
                              <div className="flex w-full justify-center item-center gap-4">
                                {/* Title */}
                                <div className="flex w-full flex-col">
                                  <label
                                    htmlFor="titleID"
                                    className="text-sm font-medium leading-6 text-gray-900 mb-1"
                                  >
                                    Title:
                                  </label>
                                  <input
                                    type="text"
                                    name="titleName"
                                    id="titleID"
                                    onChange={(e) =>
                                      handleInputChange("title", e)
                                    }
                                    value={listing.title}
                                    className="rounded-md  border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    placeholder="Enter title"
                                  />
                                </div>

                                {/* Zillow Title */}
                                <div className="flex w-full flex-col">
                                  <label
                                    htmlFor="zillowTitleID"
                                    className="text-sm font-medium leading-6 text-gray-900 mb-1"
                                  >
                                    Zillow Title:
                                  </label>
                                  <input
                                    type="text"
                                    name="zillowTitleName"
                                    id="zillowTitleID"
                                    onChange={(e) =>
                                      handleInputChange("zillowTitle", e)
                                    }
                                    value={listing.zillowTitle}
                                    className="rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    placeholder="Enter Zillow title"
                                  />
                                </div>
                              </div>
                              {/* Description */}
                              <div className="flex flex-col ">
                                <label
                                  htmlFor="desID"
                                  className="text-sm font-medium leading-6 text-gray-900 mb-1"
                                >
                                  Description:
                                </label>
                                <textarea
                                  name="desName"
                                  id="desID"
                                  rows={7}
                                  onChange={(e) =>
                                    handleInputChange("description", e)
                                  }
                                  value={listing.description}
                                  className="rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                  placeholder="Enter description"
                                />
                              </div>
                              <div className="flex mt-0">
                                <div className="mt-3">
                                  <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                  >
                                    <DemoContainer components={["DatePicker"]}>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <div style={{ marginRight: "15px" }}>
                                          <DatePicker
                                            label="Move-In Date"
                                            value={
                                              !listing.moveInDate
                                                ? null
                                                : dayjs(listing.moveInDate)
                                            }
                                            onChange={(
                                              date?:
                                                | string
                                                | number
                                                | Date
                                                | dayjs.Dayjs
                                                | null
                                                | undefined,
                                            ) => {
                                              setListingDetailsChanged(true);
                                              if (date == null) {
                                                setListing((prevListing) => ({
                                                  ...prevListing,
                                                  moveInDate: "",
                                                }));
                                              } else {
                                                setListing((prevListing) => ({
                                                  ...prevListing,
                                                  moveInDate:
                                                    date.toLocaleString(),
                                                }));
                                              }
                                            }}
                                            disabled={isVacantChecked}
                                          />
                                        </div>
                                        <div className=" relative flex items-start">
                                          <div className="ml-5   flex h-6 items-center">
                                            <input
                                              id="vacantCheckbox"
                                              name="vacantCheckbox"
                                              type="checkbox"
                                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                              onChange={handleCheckboxChange}
                                              checked={isVacantChecked}
                                            />
                                            <div className="ml-3 text-sm leading-6">
                                              <label
                                                htmlFor="vacantCheckbox"
                                                className="font-medium text-gray-900"
                                              >
                                                Is Vacant, the apartment is
                                                immediately available.
                                              </label>{" "}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </DemoContainer>
                                  </LocalizationProvider>
                                </div>
                              </div>
                            </div>

                            {/*building details*/}
                            <div className="bg-gray-100 w-[48%] max-w-3xl p-6 rounded-md">
                              <div className="flex flex-wrap gap-x-2 gap-y-6">
                                {/* Building */}
                                <div className="w-[48%] flex flex-col">
                                  <label
                                    htmlFor="titleID"
                                    className="text-sm font-medium text-gray-900 mb-1"
                                  >
                                    Building:
                                  </label>
                                  <input
                                    type="text"
                                    name="titleName"
                                    id="titleID"
                                    onChange={(e) =>
                                      handleInputChange("building", e)
                                    }
                                    value={listing.building}
                                    className="rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    placeholder="Enter building"
                                  />
                                </div>

                                {/* Line */}
                                <div className="w-[48%] flex flex-col">
                                  <label
                                    htmlFor="lineID"
                                    className="text-sm font-medium text-gray-900 mb-1"
                                  >
                                    Line:
                                  </label>
                                  <input
                                    type="text"
                                    name="lineName"
                                    id="lineID"
                                    onChange={(e) =>
                                      handleInputChange("line", e)
                                    }
                                    value={listing.line}
                                    className="rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    placeholder="Enter line"
                                  />
                                </div>

                                {/* Floor */}
                                <div className="w-[48%] flex flex-col">
                                  <label
                                    htmlFor="floorID"
                                    className="text-sm font-medium text-gray-900 mb-1"
                                  >
                                    Floor:
                                  </label>
                                  <input
                                    type="text"
                                    name="floorName"
                                    id="floorID"
                                    onChange={(e) =>
                                      handleInputChange("floor", e)
                                    }
                                    value={listing.floor}
                                    className="rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    placeholder="Enter floor"
                                  />
                                </div>

                                {/* Access Code */}
                                <div className="w-[48%] flex flex-col">
                                  <label
                                    htmlFor="accessCodeID"
                                    className="text-sm font-medium text-gray-900 mb-1"
                                  >
                                    Access Code:
                                  </label>
                                  <input
                                    type="text"
                                    name="accessCodeName"
                                    id="accessCodeID"
                                    onChange={(e) =>
                                      handleInputChange("accessCode", e)
                                    }
                                    value={listing.accessCode}
                                    className="rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    placeholder="Enter access code"
                                  />
                                </div>

                                {/* Bedrooms */}
                                <div className="w-[48%] flex flex-col">
                                  <label
                                    htmlFor="bedroomsID"
                                    className="text-sm font-medium text-gray-900 mb-1"
                                  >
                                    Bedrooms:
                                  </label>
                                  <input
                                    type="number"
                                    name="bedroomsName"
                                    id="bedroomsID"
                                    step="1"
                                    onChange={(e) =>
                                      handleInputChange("numOfBedrooms", e)
                                    }
                                    value={listing.numOfBedrooms}
                                    className="rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    placeholder="0"
                                  />
                                </div>

                                {/* Rental Price */}
                                <div className="w-[48%] flex flex-col">
                                  <label
                                    htmlFor="rentID"
                                    className="text-sm font-medium text-gray-900 mb-1"
                                  >
                                    Rental Price:
                                  </label>
                                  <input
                                    type="number"
                                    name="rentName"
                                    id="rentID"
                                    step="1"
                                    onChange={(e) =>
                                      handleInputChange("rent", e)
                                    }
                                    value={listing.rent}
                                    className="rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    placeholder="0.00"
                                  />
                                </div>

                                {/* Current Rent */}
                                <div className="w-[48%] flex flex-col">
                                  <label
                                    htmlFor="currentRentID"
                                    className="text-sm font-medium text-gray-900 mb-1"
                                  >
                                    Current Rent:
                                  </label>
                                  <input
                                    type="number"
                                    name="currentRentName"
                                    id="currentRentID"
                                    step="1"
                                    onChange={(e) =>
                                      handleInputChange("currentRent", e)
                                    }
                                    value={listing.currentRent}
                                    className="rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    placeholder="0.00"
                                  />
                                </div>

                                {/* Workflow Type */}
                                <div className="w-[48%] flex flex-col">
                                  <label
                                    htmlFor="workflowTypeID"
                                    className="text-sm font-medium text-gray-900 mb-1"
                                  >
                                    Workflow Type:
                                  </label>
                                  <select
                                    name="workflowTypeName"
                                    id="workflowTypeID"
                                    onChange={(e) =>
                                      handleInputChange("workflowType", e)
                                    }
                                    value={listing.workflowType}
                                    className="rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                  >
                                    <option value={0}>Mandatory</option>
                                    <option value={1}>Optional</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-gray-100 py-6 mt-4 rounded-md px-4">
                            <div className="w-full mt-4 flex items-center justify-center whitespace-nowrap gap-2">
                              <div className="w-5 h-5 flex items-center justify-center text-white bg-gray-900 rounded-full text-xs">
                                a
                              </div>

                              <label
                                htmlFor="email"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                Appfolio Link:
                              </label>

                              <input
                                type="text"
                                name="yourFieldName"
                                id="yourFieldId"
                                step="1"
                                onChange={(e) =>
                                  handleInputChange("appfolioLink", e)
                                }
                                value={listing?.appfolioLink}
                                className="w-full block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                placeholder="Example - https://downtown.appfolio.com/listings/detail/045fb15e-504f-4fb6-ae82-5de077c39028"
                              />
                            </div>

                            <div className="w-full mt-4 flex items-center justify-center whitespace-nowrap gap-1">
                              <YouTubeIcon className="w-4 h-4 text-gray-900 mt-1" />{" "}
                              {/* Proper size + color + alignment */}
                              <label
                                htmlFor="email"
                                className="block text-sm font-medium leading-6 text-gray-900 mt-1"
                              >
                                YouTube Link:
                              </label>
                              <input
                                type="text"
                                name="yourFieldName"
                                id="yourFieldId"
                                step="1"
                                onChange={(e) =>
                                  handleInputChange("youTubeLink", e)
                                }
                                value={listing?.youTubeLink}
                                className="w-full block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                placeholder="Example - https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center m-4 justify-center gap-6">
                      <button
                        type="button"
                        className="w-fit inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        onClick={() => {
                          setEditListings([]);
                          setOpen(false);
                          setIsVacantChecked(false);
                        }}
                        ref={cancelButtonRef}
                      >
                        Cancel
                      </button>

                      <button
                        disabled={isDisabled}
                        type="button"
                        className={`inline-flex justify-center rounded-md ${
                          isDisabled
                            ? "bg-gray-300 cursor-not-allowed"
                            : "inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        } px-6 py-2 text-sm font-semibold text-white shadow-sm ${
                          isDisabled ? "text-gray-500" : "text-white"
                        }`}
                        onClick={() => {
                          SaveChanges();
                          setEditListings([]);
                        }}
                      >
                        Save
                      </button>
                    </div>
                    {loading && (
                      <div className="absolute bg-gray-300/80 top-0 left-0 w-full h-full flex items-center justify-center">
                        <Loading />
                      </div>
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <Notification
        message={notificationMessage}
        messageType={notificationMessageType}
        isOpen={openNotification}
        setIsOpen={setOpenNotification}
      />
    </>
  );
}
